import { useState, useEffect, useRef } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { Button } from "@/components/ui/button";
import { Link } from "wouter";
import whiteLogo from "@assets/openbox-white_1763001201526.png";
import aGradeImg from "@assets/a_1763307959198.png";
import bPlusGradeImg from "@assets/b_1763307959198.png";
import bMinusGradeImg from "@assets/c_1763307959198.png";
import transparencyVideo from "@assets/Screen Recording 2025-11-28 at 09.26.50_1764362555850.webm";
import { GRADE_DEFECT_CEILINGS } from "@shared/grading";
import { X } from "lucide-react";

const WORDS = ["brand", "model", "quantity", "condition"];
const TYPING_SPEED = 100;
const DELETING_SPEED = 60;
const PAUSE_AFTER_WORD = 1500;
const PAUSE_BEFORE_DELETE = 800;

function TypewriterText() {
  const [wordIndex, setWordIndex] = useState(0);
  const [displayText, setDisplayText] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const currentWord = WORDS[wordIndex];
    
    const timeout = setTimeout(() => {
      if (!isDeleting) {
        if (displayText.length < currentWord.length) {
          setDisplayText(currentWord.slice(0, displayText.length + 1));
        } else {
          setTimeout(() => setIsDeleting(true), PAUSE_AFTER_WORD);
        }
      } else {
        if (displayText.length > 0) {
          setDisplayText(displayText.slice(0, -1));
        } else {
          setIsDeleting(false);
          setWordIndex((prev) => (prev + 1) % WORDS.length);
        }
      }
    }, isDeleting ? DELETING_SPEED : (displayText.length === WORDS[wordIndex].length ? PAUSE_BEFORE_DELETE : TYPING_SPEED));

    return () => clearTimeout(timeout);
  }, [displayText, isDeleting, wordIndex]);

  return (
    <div className="pl-[0px] pr-[0px] ml-[27px] mr-[27px]">
      <p className="text-2xl md:text-3xl font-bold text-white mb-1">Choose your</p>
      <p className="text-3xl md:text-4xl font-bold">
        <span className="text-[#7db428]">{displayText}</span>
        <span className="text-[#7db428] animate-pulse">|</span>
      </p>
    </div>
  );
}

interface SplashWelcomeModalProps {
  open: boolean;
  onClose: () => void;
}

export function SplashWelcomeModal({ open, onClose }: SplashWelcomeModalProps) {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (open && videoRef.current) {
      videoRef.current.play().catch(() => {});
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent 
        className="max-w-[75vw] w-full max-h-[90vh] overflow-y-auto p-0 bg-[#1a1a1a] border-gray-700"
        aria-describedby={undefined}
      >
        <VisuallyHidden>
          <DialogTitle>Welcome to OpenBox by ShopJimmy</DialogTitle>
        </VisuallyHidden>
        <div className="relative">
          <div 
            className="absolute inset-0 pointer-events-none"
            style={{
              background: `
                radial-gradient(circle at 20% 30%, rgba(125, 180, 40, 0.15) 0%, transparent 50%),
                radial-gradient(circle at 80% 70%, rgba(125, 180, 40, 0.1) 0%, transparent 50%)
              `
            }}
          />
          
          <button
            onClick={onClose}
            className="absolute top-4 right-4 z-50 p-2 rounded-full bg-gray-800/80 hover:bg-gray-700 transition-colors text-gray-300 hover:text-white"
            data-testid="button-close-welcome"
            aria-label="Close welcome modal"
          >
            <X className="h-5 w-5" />
          </button>

          <div className="relative z-10 p-8 md:p-12">
            <div className="flex flex-col md:flex-row items-center md:items-start justify-between gap-6 mb-8">
              <div className="text-left">
                <p className="text-base md:text-lg font-medium italic text-white tracking-tight mb-2">📣 introducing...</p>
                <img 
                  src={whiteLogo} 
                  alt="ShopJimmy Open Box Logo" 
                  className="h-14 md:h-16 w-auto object-contain drop-shadow-[0_0_30px_rgba(125,180,40,0.5)]"
                />
              </div>

              <div className="text-center md:text-right">
                <TypewriterText />
              </div>
            </div>

            <div className="mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-white text-center mt-[0px] mb-[0px]">
                Transparency You Can See
              </h2>
              <p className="text-center text-gray-400 mt-[8px] mb-[8px]">
                All refrigerators work like new. The only difference is cosmetic appearance.
              </p>

              <div className="mb-6 relative rounded-xl overflow-hidden border border-gray-700">
                <video
                  ref={videoRef}
                  src={transparencyVideo}
                  autoPlay
                  muted
                  loop
                  playsInline
                  className="w-full h-auto"
                  data-testid="video-modal-transparency"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {[
                  { 
                    grade: 'A GRADE', 
                    desc: 'Looks New', 
                    details: '✅ No front-facing flaws',
                    badgeColors: 'bg-green-100 text-green-800 border-green-300',
                    image: aGradeImg
                  },
                  { 
                    grade: 'B+ GRADE', 
                    desc: 'Minor Imperfections', 
                    details: `⚪ ${GRADE_DEFECT_CEILINGS['B+']} or fewer flaws`,
                    badgeColors: 'bg-blue-100 text-blue-800 border-blue-300',
                    image: bPlusGradeImg
                  },
                  { 
                    grade: 'B– GRADE', 
                    desc: 'Noticeable Wear', 
                    details: `⚫ Up to ${GRADE_DEFECT_CEILINGS['B-']} flaws`,
                    badgeColors: 'bg-yellow-100 text-yellow-800 border-yellow-300',
                    image: bMinusGradeImg
                  }
                ].map((item, idx) => (
                  <div 
                    key={idx}
                    className="bg-gradient-to-br from-[#2d2d2d] to-[#252525] border border-gray-700 rounded-xl overflow-hidden"
                    data-testid={`modal-grade-card-${idx}`}
                  >
                    <div className="h-32 md:h-40 overflow-hidden bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center">
                      <img 
                        src={item.image} 
                        alt={`${item.grade} example`}
                        className="h-full w-auto object-contain"
                      />
                    </div>
                    <div className="p-4">
                      <div className={`inline-block ${item.badgeColors} border px-3 py-1 rounded-full font-bold text-sm mb-2`}>
                        {item.grade}
                      </div>
                      <div className="text-sm text-slate-300 font-medium">
                        {item.desc}
                      </div>
                      <div className="text-xs text-slate-400 mt-1">
                        {item.details}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
              {[
                { icon: '💰', title: 'Real Savings', desc: 'Top brands, lower prices' },
                { icon: '🛡️', title: '1-Year Warranty', desc: 'Factory protection included' },
                { icon: '🔍', title: 'Full Transparency', desc: 'Videos of every serialized unit' },
                { icon: '♻️', title: 'Sustainable', desc: 'Premium products, less waste' }
              ].map((benefit, idx) => (
                <div 
                  key={idx}
                  className="bg-[rgba(45,45,45,0.5)] border border-gray-700 rounded-lg p-4 text-center"
                  data-testid={`modal-benefit-${idx}`}
                >
                  <span className="text-2xl mb-2 block">{benefit.icon}</span>
                  <h4 className="text-sm font-bold text-white mb-1">{benefit.title}</h4>
                  <p className="text-xs text-slate-400">{benefit.desc}</p>
                </div>
              ))}
            </div>

            <div className="text-center">
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Button
                  size="lg"
                  onClick={onClose}
                  className="bg-gradient-to-r from-[#7DB428] to-[#8BC34A] hover:from-[#6da020] hover:to-[#7ab33a] text-white text-lg font-bold px-10 py-6 rounded-full shadow-[0_8px_30px_rgba(125,180,40,0.4)] hover:shadow-[0_12px_40px_rgba(125,180,40,0.6)] transition-all duration-300 hover:-translate-y-1"
                  data-testid="button-enter-showroom"
                >
                  Enter Showroom →
                </Button>
                <Link href="/signup?type=business" onClick={onClose}>
                  <Button
                    size="lg"
                    className="bg-red-600 hover:bg-red-700 text-white font-bold px-8 py-6 rounded-full shadow-[0_8px_30px_rgba(220,38,38,0.4)] hover:shadow-[0_12px_40px_rgba(220,38,38,0.6)] transition-all duration-300 hover:-translate-y-1"
                    data-testid="button-modal-signup"
                  >
                    Sign Up for B2B Access
                  </Button>
                </Link>
              </div>
              <p className="text-sm text-slate-500 mt-4">
                Already have an account? <Link href="/login" onClick={onClose} className="text-[#7db428] hover:underline" data-testid="link-modal-login">Log in</Link>
              </p>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
